import { Box, Button, Grid, Pagination, Stack, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { confirmAlert } from "react-confirm-alert";
import { ApiError } from "../../Api/ApiError";
import { delNewsData, getNewsDetailList, regNewsLimit, regNewsRelease } from "../../Api/News";
import { NewsData } from "../../Objects/NewsDate";
import PageTitle from "../atoms/PageTitle";
import NewsCard from "../molecules/NewsCard";
import NewsDetailDialog from "../organisms/NewsDetailDialog";
import { NewsEditDialog } from "../organisms/NewsEditDialog";

const NewsPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar()

    const [page, setPage] = React.useState<number>(1)
    const [limit] = React.useState<number>(12)

    const [newsList, setNewsList] = React.useState<NewsData[]>()
    const [rows, setRows] = React.useState<number>()

    const [newsDetail, setNewsDetail] = React.useState<NewsData>()

    const [editNews, setEditNews] = React.useState<number>()

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const loadData = React.useCallback(() => {
        getNewsDetailList({
            page: page,
            limit: limit
        }, {signal: abortController.signal}).then(result => {
            setNewsList(result.list)
            setRows(result.rows)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [abortController, page, limit, enqueueSnackbar])

    const doRegNewsRelease = React.useCallback((newsData: NewsData, isPush: boolean) => {
        if (newsData.news_id) {
            regNewsRelease({
                news_id: newsData.news_id,
                is_push: isPush
            }, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("登録しました", { variant: "success" })
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            }).finally(() => {
                loadData()
            })
        }
    }, [abortController, loadData, enqueueSnackbar])

    const doRegNewsLimit = React.useCallback((newsData: NewsData) => {
        if (newsData.news_id) {
            regNewsLimit({
                news_id: newsData.news_id
            }, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("登録しました", { variant: "success" })
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            }).finally(() => {
                loadData()
            })
        }
    }, [abortController, loadData, enqueueSnackbar])

    const doDeleteNews = React.useCallback((newsData: NewsData) => {
        if (newsData.news_id) {
            delNewsData({
                news_id: newsData.news_id
            }, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("削除しました", { variant: "success" })
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            }).finally(() => {
                loadData()
            })
        }
    }, [abortController, loadData, enqueueSnackbar])

    React.useEffect(() => {
        loadData()
    }, [loadData])
    return (
        <>
            <PageTitle text="ニュース管理" />
            <Stack
                direction={"row"}
                justifyContent={"right"}
                my={2}
            >
                <Button
                    variant="contained"
                    onClick={() => {
                        setEditNews(0)
                    }}
                >新しくニュースを登録する</Button>
            </Stack>
            {newsList ? (
                <Stack
                    spacing={2}
                >
                    {rows &&
                        <Box>
                            <Stack
                                direction={"row"}
                            >
                                <Pagination
                                    count={Math.ceil(rows / limit)}
                                    page={page}
                                    variant="outlined"
                                    onChange={(e, page) => {
                                        setPage(page)
                                    }}
                                />
                            </Stack>
                        </Box>
                    }
                    <Box>
                        <Grid container
                            spacing={2}
                            alignItems="stretch"
                        >
                            {newsList.map(newsData => (
                                <Grid item key={newsData.news_id}
                                    xs={12} sm={6} md={4}
                                >
                                    <NewsCard
                                        newsData={newsData}
                                        onShowDetail={newsData => {
                                            setNewsDetail(newsData)
                                        }}
                                        onRegNewsRelease={newsData => {
                                            confirmAlert({
                                                title: "ニュース公開",
                                                message: "ニュースを公開します。プッシュ通知を行いますか？",
                                                buttons: [
                                                    {
                                                        label: "プッシュ通知をする",
                                                        onClick: () => {
                                                            doRegNewsRelease(newsData, true)
                                                        }
                                                    }, {
                                                        label: "プッシュ通知をしない",
                                                        onClick: () => {
                                                            doRegNewsRelease(newsData, false)
                                                        }
                                                    }, {
                                                        label: "キャンセル",
                                                        onClick: () => { }
                                                    }
                                                ]
                                            })
                                        }}
                                        onRegNewsLimit={newsData => {
                                            doRegNewsLimit(newsData)
                                        }}
                                        onDelete={newsData => {
                                            confirmAlert({
                                                title: "ニュース削除",
                                                message: "ニュースを削除します。この操作は取り消せません",
                                                buttons: [
                                                    {
                                                        label: "削除",
                                                        onClick: () => {
                                                            doDeleteNews(newsData)
                                                        }
                                                    }, {
                                                        label: "キャンセル",
                                                        onClick: () => { }
                                                    }
                                                ]
                                            })

                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Stack>
            ) : (
                <Typography>loading...</Typography>
            )}
            <NewsDetailDialog
                newsData={newsDetail}
                onClose={() => {
                    setNewsDetail(undefined)
                }}
            />
            <NewsEditDialog
                news_id={editNews}
                onClose={() => {
                    setEditNews(undefined)
                    loadData()
                }}
            />
        </>
    )
}

export default NewsPage